'use client';

import { c as _c } from "react/compiler-runtime";
import Link from 'next/link';
import { ShopifyImage as ShopifyImg } from '@/components/ui/shopify-image';
export interface SwatchGroupItem {
  /** Product GID; lets the PDP merge live availability into cached swatch data. */
  productId: string;
  handle: string;
  title: string;
  /** Display name for the swatch (metaobject label, falls back to product title). */
  label: string;
  /**
   * Dedicated swatch image from a `shopify--color-pattern` metaobject
   * (variant `custom.swatch_color` or apparel Color category).
   * Takes precedence over {@link swatchColor} when both exist.
   */
  swatchImageUrl: string | null;
  /**
   * Solid CSS color from a `shopify--color-pattern` metaobject when no
   * dedicated swatch image is set.
   */
  swatchColor: string | null;
  /**
   * Sibling PDP hero image, warmed from the swatch row so navigating to the
   * sibling paints its gallery from browser cache instead of flashing.
   */
  heroImageUrl?: string | null;
  /** Corner flag on the tile from a `swatch-badge:*` tag, e.g. "Best Value". */
  badge?: string | null;
  available: boolean;
  isCurrent: boolean;
}
interface ProductSwatchGroupProps {
  items: SwatchGroupItem[];
  /**
   * Heading for the selector row. Use "Design" for print/pattern apparel groups
   * (`swatch-group:*`) and "Flavor" for supplement flavor groups (`flavor-group:*`
   * or electrolyte product types).
   */
  groupLabel?: string;
}

/** Image or solid-color fill for a swatch tile; gray when neither is set. */
function SwatchFill(t0) {
  const $ = _c(8);
  const {
    item,
    imageSizes
  } = t0;
  if (item.swatchImageUrl) {
    let t1;
    if ($[0] !== imageSizes || $[1] !== item.label || $[2] !== item.swatchImageUrl) {
      t1 = <ShopifyImg src={item.swatchImageUrl} alt={item.label} fill={true} className="object-cover" sizes={imageSizes} />;
      $[0] = imageSizes;
      $[1] = item.label;
      $[2] = item.swatchImageUrl;
      $[3] = t1;
    } else {
      t1 = $[3];
    }
    return t1;
  }
  let t1;
  if ($[4] !== item.swatchColor) {
    t1 = item.swatchColor ? {
      backgroundColor: item.swatchColor
    } : undefined;
    $[4] = item.swatchColor;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== t1) {
    t2 = <span className="block h-full w-full" style={t1} />;
    $[6] = t1;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  return t2;
}
function SoldOutSlash() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <span className="pointer-events-none absolute inset-0 flex items-center justify-center"><span className="block h-full w-px rotate-45 bg-neutral-400" /></span>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}

/**
 * Flavor tile — labeled card (image with the flavor name strip below) matching
 * the Style selector tiles exactly (size, borders, label type, corner badge).
 */
function FlavorSwatch(t0) {
  const $ = _c(23);
  const {
    item
  } = t0;
  let t1;
  if ($[0] !== item) {
    t1 = <SwatchFill item={item} imageSizes="(max-width: 767px) 64px, 72px" />;
    $[0] = item;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== item.badge) {
    t2 = item.badge && <span className="absolute left-0 top-0 rounded-br bg-brand-accent px-1 py-0.5 text-[8px] font-bold leading-tight text-white md:text-[9px]">{item.badge}</span>;
    $[2] = item.badge;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t1 || $[5] !== t2) {
    t3 = <span className="relative block h-14 w-full overflow-hidden bg-neutral-100 md:h-16">{t1}{t2}</span>;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== item.label) {
    t4 = <span className="block w-full border-t border-neutral-100 px-0.5 py-1 text-center text-[9px] font-medium text-neutral-900 md:text-[10px]">{item.label}</span>;
    $[7] = item.label;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== t3 || $[10] !== t4) {
    t5 = <>{t3}{t4}</>;
    $[9] = t3;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const tile = t5;
  if (item.isCurrent) {
    let t6;
    if ($[12] !== item.label || $[13] !== tile) {
      t6 = <div className="w-16 overflow-hidden rounded-sm border border-neutral-900 md:w-[4.5rem]" title={item.label}>{tile}</div>;
      $[12] = item.label;
      $[13] = tile;
      $[14] = t6;
    } else {
      t6 = $[14];
    }
    return t6;
  }
  const t6 = `/products/${item.handle}`;
  const t7 = `relative w-16 overflow-hidden rounded-sm border border-neutral-300 transition-colors hover:border-neutral-400 md:w-[4.5rem] ${item.available ? "" : "opacity-40"}`;
  const t8 = item.available ? item.label : `${item.label} — Sold out`;
  let t9;
  if ($[15] !== item.available) {
    t9 = !item.available && <SoldOutSlash />;
    $[15] = item.available;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t6 || $[18] !== t7 || $[19] !== t8 || $[20] !== t9 || $[21] !== tile) {
    t10 = <Link href={t6} scroll={false} className={t7} title={t8}>{tile}{t9}</Link>;
    $[17] = t6;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
    $[21] = tile;
    $[22] = t10;
  } else {
    t10 = $[22];
  }
  return t10;
}

/** Design tile — compact circle swatch for print/pattern apparel groups. */
function DesignSwatch(t0) {
  const $ = _c(22);
  const {
    item
  } = t0;
  const t1 = `relative block h-9 w-9 shrink-0 overflow-hidden rounded-full border border-neutral-300 ${item.isCurrent ? "ring-2 ring-neutral-900 ring-offset-2" : "group-hover:ring-2 group-hover:ring-neutral-300 group-hover:ring-offset-2"}`;
  let t2;
  if ($[0] !== item.swatchColor || $[1] !== item.swatchImageUrl) {
    t2 = item.swatchColor && !item.swatchImageUrl ? {
      backgroundColor: item.swatchColor
    } : undefined;
    $[0] = item.swatchColor;
    $[1] = item.swatchImageUrl;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== item.label || $[4] !== item.swatchColor || $[5] !== item.swatchImageUrl) {
    t3 = item.swatchImageUrl ? <ShopifyImg src={item.swatchImageUrl} alt={item.label} fill={true} className="object-cover" sizes="36px" /> : !item.swatchColor ? <span className="block h-full w-full bg-neutral-100" /> : null;
    $[3] = item.label;
    $[4] = item.swatchColor;
    $[5] = item.swatchImageUrl;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t1 || $[8] !== t2 || $[9] !== t3) {
    t4 = <span className={t1} style={t2}>{t3}</span>;
    $[7] = t1;
    $[8] = t2;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const circle = t4;
  if (item.isCurrent) {
    let t5;
    if ($[11] !== circle || $[12] !== item.label) {
      t5 = <div className="group relative flex h-10 w-10 shrink-0 items-center justify-center" title={item.label}>{circle}</div>;
      $[11] = circle;
      $[12] = item.label;
      $[13] = t5;
    } else {
      t5 = $[13];
    }
    return t5;
  }
  const t5 = `/products/${item.handle}`;
  const t6 = `group relative flex h-10 w-10 shrink-0 items-center justify-center transition-opacity ${item.available ? "" : "opacity-40"}`;
  const t7 = item.available ? item.label : `${item.label} — Sold out`;
  let t8;
  if ($[14] !== item.available) {
    t8 = !item.available && <span className="absolute inset-0 flex items-center justify-center"><span className="block h-10 w-px rotate-45 bg-neutral-400" /></span>;
    $[14] = item.available;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== circle || $[17] !== t5 || $[18] !== t6 || $[19] !== t7 || $[20] !== t8) {
    t9 = <Link href={t5} scroll={false} className={t6} title={t7}>{circle}{t8}</Link>;
    $[16] = circle;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  return t9;
}

/**
 * Matches both PDP gallery hero `sizes` (mobile carousel and desktop grid — see
 * product-gallery.tsx) so warmed sibling heroes resolve to the exact srcset
 * candidate the gallery will request after navigation.
 */
const GALLERY_HERO_SIZES = '(max-width: 1023px) calc(100vw - 2.75rem), 33vw';

/**
 * Renders one swatch (image or solid color) per product in a `swatch-group:*` /
 * `flavor-group:*` tag set. Each sibling product is a separate listing (no
 * Shopify color/flavor option), so a swatch links to that product's page rather
 * than selecting a variant. The current product's swatch is shown selected and
 * is not a link.
 *
 * Design groups use compact circles; Flavor groups use compact square image
 * cards (no label strip). Visual priority: dedicated image → solid color → gray.
 */
export function ProductSwatchGroup(t0) {
  const $ = _c(24);
  const {
    items,
    groupLabel: t1
  } = t0;
  const groupLabel = t1 === undefined ? "Design" : t1;
  if (items.length <= 1) {
    return null;
  }
  let t2;
  if ($[0] !== items) {
    t2 = items.find(_temp);
    $[0] = items;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const current = t2;
  let t3;
  if ($[2] !== groupLabel) {
    t3 = groupLabel.toLowerCase();
    $[2] = groupLabel;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const isFlavor = t3 === "flavor";
  let t4;
  if ($[4] !== current || $[5] !== groupLabel || $[6] !== isFlavor || $[7] !== items) {
    const heroWarmItems = isFlavor ? items.filter(_temp2) : [];
    let t5;
    if ($[9] !== groupLabel) {
      t5 = <span className="text-[13px] font-bold uppercase tracking-wide text-neutral-900">{groupLabel}:</span>;
      $[9] = groupLabel;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    let t6;
    if ($[11] !== current) {
      t6 = current?.label && <span className="text-sm text-neutral-600">{current.label}</span>;
      $[11] = current;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    let t7;
    if ($[13] !== t5 || $[14] !== t6) {
      t7 = <div className="mb-2.5 flex items-baseline gap-1.5">{t5}{t6}</div>;
      $[13] = t5;
      $[14] = t6;
      $[15] = t7;
    } else {
      t7 = $[15];
    }
    const t8 = isFlavor ? "flex flex-wrap gap-3" : "flex flex-wrap gap-2";
    let t9;
    if ($[16] !== isFlavor || $[17] !== items) {
      let t10;
      if ($[19] !== isFlavor) {
        t10 = item_1 => isFlavor ? <FlavorSwatch key={item_1.handle} item={item_1} /> : <DesignSwatch key={item_1.handle} item={item_1} />;
        $[19] = isFlavor;
        $[20] = t10;
      } else {
        t10 = $[20];
      }
      t9 = items.map(t10);
      $[16] = isFlavor;
      $[17] = items;
      $[18] = t9;
    } else {
      t9 = $[18];
    }
    let t10;
    if ($[21] !== t8 || $[22] !== t9) {
      t10 = <div className={t8}>{t9}</div>;
      $[21] = t8;
      $[22] = t9;
      $[23] = t10;
    } else {
      t10 = $[23];
    }
    t4 = <div>{t7}{t10}{heroWarmItems.length > 0 && <span aria-hidden={true} className="pointer-events-none absolute h-px w-px overflow-hidden opacity-0">{heroWarmItems.map(_temp3)}</span>}</div>;
    $[4] = current;
    $[5] = groupLabel;
    $[6] = isFlavor;
    $[7] = items;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}
function _temp3(item_2) {
  return <ShopifyImg key={item_2.handle} src={item_2.heroImageUrl as string} alt="" fill={true} loading="eager" fetchPriority="low" sizes={GALLERY_HERO_SIZES} />;
}
function _temp2(item_0) {
  return !item_0.isCurrent && item_0.heroImageUrl;
}
function _temp(item) {
  return item.isCurrent;
}